import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTranslations } from "@/hooks/use-translations";
import { api, ApiError, type ChatHistoryMessage, type CreativeAction, type CreativeView } from "@/lib/api";

/** What a creative-* chat message carries in its toolCall, as the server writes it. */
export type CreativeCardData = {
	kind: string;
	creativeId: string;
	taskId: string | null;
	mediaType: "image" | "video";
	/** Only a result card carries its file. */
	fileId?: string | null;
	actions: CreativeAction[];
};

export function creativeCardOf(message: ChatHistoryMessage): CreativeCardData | null {
	if (message.role !== "assistant") return null;
	const card = message.toolCall as Partial<CreativeCardData> | null;
	if (!card || typeof card.kind !== "string" || !card.kind.startsWith("creative-") || typeof card.creativeId !== "string") {
		return null;
	}
	return {
		kind: card.kind,
		creativeId: card.creativeId,
		taskId: card.taskId ?? null,
		mediaType: card.mediaType === "video" ? "video" : "image",
		fileId: card.fileId ?? null,
		actions: Array.isArray(card.actions) ? card.actions : [],
	};
}

/**
 * The message ids whose buttons still answer: per creative, the newest card
 * that has buttons. A card without buttons (accepted, expired) does not take
 * that over, so after the 24 hours the result's buttons keep working; the
 * server's state then decides whether they are enabled.
 */
export function answerableCreativeCards(messages: ChatHistoryMessage[]): Set<string> {
	const newest = new Map<string, string>();
	for (const message of messages) {
		const card = creativeCardOf(message);
		if (card?.actions.length) newest.set(card.creativeId, message.id);
	}
	return new Set(newest.values());
}

const KIND_TONE: Record<string, BadgeProps["tone"]> = {
	"creative-accepted": "success",
	"creative-accept-failed": "danger",
	"creative-failed": "danger",
	"creative-ad-rejected": "danger",
	"creative-unavailable": "warning",
	"creative-expired": "neutral",
	"creative-rejected": "neutral",
};

/** Reject and No end something; the rest go on with the creative. */
const QUIET_ACTIONS = new Set<CreativeAction>(["reject", "no"]);

const humanize = (code: string) => code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, " ");

function CreativePreview({ conversationId, fileId, mediaType }: { conversationId: string; fileId: string; mediaType: "image" | "video" }) {
	const t = useTranslations("workspace");
	// A signed link lives 15 minutes; a fresh one is fetched before that, or when the media fails to load.
	const link = useQuery({
		queryKey: ["creativePreview", conversationId, fileId],
		queryFn: () => api.getProjectFileSignedUrl(conversationId, fileId),
		staleTime: 10 * 60_000,
		retry: 1,
	});
	const reload = () => {
		if (!link.isFetching) void link.refetch();
	};
	if (link.isLoading) return <div className="mt-3 h-48 w-full animate-pulse rounded-lg bg-bg-sunken" />;
	if (!link.data) return <p className="mt-3 text-xs text-danger">{t("creative.previewFailed")}</p>;
	return mediaType === "video" ? (
		<video
			src={link.data.signedUrl}
			controls
			preload="metadata"
			onError={reload}
			className="mt-3 max-h-80 max-w-full rounded-lg bg-bg-sunken"
		/>
	) : (
		<img
			src={link.data.signedUrl}
			alt={t("creative.previewAlt")}
			onError={reload}
			className="mt-3 max-h-80 max-w-full rounded-lg bg-bg-sunken object-contain"
		/>
	);
}

/** The card under an assistant message, when the message is about a generated creative. */
export function CreativeCardFor({ message, answerable }: { message: ChatHistoryMessage; answerable: Set<string> }) {
	const card = creativeCardOf(message);
	return card ? <CreativeCard message={message} card={card} answerable={answerable.has(message.id)} /> : null;
}

/**
 * The buttons and preview of a generated creative under its chat message. The
 * server decides who may press what; the card asks it for the creative's
 * state, so a button only shows as available when pressing it can work.
 */
export function CreativeCard({
	message,
	card,
	answerable,
}: {
	message: ChatHistoryMessage;
	card: CreativeCardData;
	/** The newest card of this creative that has buttons. */
	answerable: boolean;
}) {
	const t = useTranslations("workspace");
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();
	const [commentFor, setCommentFor] = useState<CreativeAction | null>(null);
	const [comment, setComment] = useState("");

	const view = useQuery({
		queryKey: ["creative", card.creativeId],
		queryFn: () => api.getCreative(card.creativeId),
		enabled: answerable,
		retry: false,
	});
	const state: CreativeView | undefined = view.data;
	const current = answerable && state?.taskId === card.taskId;
	const canAct = current && (state?.status === "waiting" || state?.status === "expired");
	// Stop is for whatever this creative is generating now: a retry replaces the card's own task.
	const stoppable = card.actions.includes("stop");
	const generating = answerable && stoppable && state?.status === "generating";
	const canStop = generating && !state?.stopping;
	const live = stoppable ? canStop : canAct;

	const decide = useMutation({
		mutationFn: (input: { action: CreativeAction; comment?: string }) =>
			api.decideCreative(card.creativeId, { ...input, taskId: input.action === "stop" ? undefined : (card.taskId ?? undefined) }),
		onSuccess: (_result, input) => {
			setCommentFor(null);
			setComment("");
			toast.info(t(`creative.sent.${input.action}`));
		},
		onError: (error) => {
			if (error instanceof ApiError && error.code === "stale_card") toast.info(t("creative.stale"));
			else if (error instanceof ApiError && error.code === "cannot_accept") toast.info(t("creative.cannotAccept"));
			else toast.error(error instanceof Error ? error.message : t("creative.failed"));
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["creative", card.creativeId] });
			queryClient.invalidateQueries({ queryKey: ["chatHistory", message.conversationId] });
		},
	});

	// Edit always needs the change; so does a new one after the user was asked what to change.
	const needsComment = (action: CreativeAction) => action === "edit" || (action === "generate-new" && state?.waitingFor === "change");
	const press = (action: CreativeAction) => {
		if (needsComment(action)) {
			setCommentFor(action);
			return;
		}
		decide.mutate({ action });
	};

	const tone = KIND_TONE[card.kind];
	const copy = card.kind === "creative-result" && canAct ? state?.adCopy : undefined;
	const locked = !state?.canAccept && card.actions.includes("accept");

	return (
		<div className="mt-3 rounded-2xl border border-border-subtle bg-bg-surface p-3 text-sm text-text-primary">
			<div className="flex flex-wrap items-center gap-2">
				{card.fileId && <Badge tone="ai">{t("aiGenerated")}</Badge>}
				{tone && <Badge tone={tone}>{t(`creative.kinds.${card.kind}`)}</Badge>}
			</div>

			{card.fileId && <CreativePreview conversationId={message.conversationId} fileId={card.fileId} mediaType={card.mediaType} />}

			{copy && (
				<dl className="mt-3 space-y-1 text-xs">
					<div>
						<dt className="text-text-tertiary">{t("creative.copy.message")}</dt>
						<dd className="whitespace-pre-line">{copy.message}</dd>
					</div>
					{copy.headline && (
						<div>
							<dt className="text-text-tertiary">{t("creative.copy.headline")}</dt>
							<dd>{copy.headline}</dd>
						</div>
					)}
					{copy.cta && (
						<div>
							<dt className="text-text-tertiary">{t("creative.copy.cta")}</dt>
							<dd>{t(`creative.cta.${copy.cta}`, { defaultValue: humanize(copy.cta) })}</dd>
						</div>
					)}
					{copy.link && (
						<div>
							<dt className="text-text-tertiary">{t("creative.copy.link")}</dt>
							<dd className="break-all">{copy.link}</dd>
						</div>
					)}
				</dl>
			)}

			{current && state?.status === "accepting" && (
				<p role="status" className="mt-3 flex items-center gap-2 text-info">
					<Loader2 size={14} className="animate-spin" />
					{t("creative.launching")}
				</p>
			)}

			{generating && state?.stopping && (
				<p role="status" className="mt-3 flex items-center gap-2 text-info">
					<Loader2 size={14} className="animate-spin" />
					{t("creative.stopping")}
				</p>
			)}

			{live && card.actions.length > 0 && (
				<div className="mt-3 flex flex-wrap gap-2">
					{card.actions.map((action) => (
						<Button
							key={action}
							size="sm"
							variant={QUIET_ACTIONS.has(action) ? "ghost" : "secondary"}
							disabled={decide.isPending || (action === "accept" && !state?.canAccept)}
							onClick={() => press(action)}
						>
							{t(`creative.actions.${action}`)}
						</Button>
					))}
				</div>
			)}

			{canAct && commentFor && (
				<div className="mt-3 space-y-2">
					<Textarea
						label={t(`creative.commentLabel.${commentFor}`)}
						value={comment}
						onChange={(event) => setComment(event.target.value)}
						maxLength={4000}
						rows={3}
					/>
					<div className="flex gap-2">
						<Button
							size="sm"
							variant="secondary"
							disabled={decide.isPending || !comment.trim()}
							onClick={() => decide.mutate({ action: commentFor, comment: comment.trim() })}
						>
							{t("creative.send")}
						</Button>
						<Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => setCommentFor(null)}>
							{t("creative.cancel")}
						</Button>
					</div>
				</div>
			)}

			{canAct && locked && (
				<p className="mt-2 text-xs text-text-tertiary">
					{state?.acceptors.length ? t("creative.onlyAcceptors", { names: state.acceptors.join(", ") }) : t("creative.cannotAccept")}
				</p>
			)}
			{canAct && state?.status === "waiting" && state.decideBy && (
				<p className="mt-2 text-xs text-text-tertiary">
					{t("creative.decideBy", { time: new Date(state.decideBy).toLocaleString(i18n.language) })}
				</p>
			)}
			{canAct && state?.status === "expired" && <p className="mt-2 text-xs text-text-tertiary">{t("creative.expiredNote")}</p>}
		</div>
	);
}
